import { useEffect, useState, useRef } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { currency } from "@/lib/status";
import { Plus, ShieldCheck, Pencil, Trash2, Car, Link2, Upload, Loader2, Sparkles, Store, Rss, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FEED_FIELDS = [["year", "Year"], ["make", "Make"], ["model", "Model"], ["trim", "Trim"], ["color", "Color"], ["msrp", "Price / MSRP"], ["vin", "VIN"], ["stock_number", "Stock #"], ["dealer_location", "Location"], ["body_type", "Body type"], ["image_url", "Image URL"], ["images", "Image gallery"], ["description", "Description"]];

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const SYNC_SOURCE = { url: "feed URL", file: "file upload", auto: "nightly auto-sync" };

const EMPTY = { year: "", make: "", model: "", trim: "", color: "", msrp: "", vin: "", stock_number: "", dealer_location: "", body_type: "", option_packages: "", image_url: "", images: [], broker_verified: false, availability: "available", description: "" };

export function InventoryManager({ scope = "staff" }) {
  const isPartner = scope === "partner";
  const { user } = useAuth();
  const listEndpoint = isPartner ? "/partner/vehicles" : "/vehicles";
  const [vehicles, setVehicles] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [importInfo, setImportInfo] = useState(null);
  const [decoding, setDecoding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dragIdx = useRef(null);
  const fileInputRef = useRef(null);
  const [feedOpen, setFeedOpen] = useState(false);
  const [feedUrl, setFeedUrl] = useState("");
  const [feedPreview, setFeedPreview] = useState(null);
  const [feedBusy, setFeedBusy] = useState(false);
  const [feedFile, setFeedFile] = useState(null);
  const [feedHeaders, setFeedHeaders] = useState([]);
  const [feedMapping, setFeedMapping] = useState({});
  const [syncStatus, setSyncStatus] = useState(null);
  const feedFileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const decodeVin = async () => {
    const vin = (form.vin || "").trim();
    if (vin.length !== 17) { toast.error("Enter a valid 17-character VIN"); return; }
    setDecoding(true);
    try {
      const { data } = await api.post("/vehicles/decode-vin", { vin });
      setForm((f) => ({ ...f, year: data.year || f.year, make: data.make || f.make, model: data.model || f.model, trim: data.trim || f.trim, body_type: data.body_type || f.body_type }));
      toast.success(`Decoded: ${[data.year, data.make, data.model].filter(Boolean).join(" ")}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not decode that VIN"); }
    finally { setDecoding(false); }
  };

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/vehicles/upload-photo", fd);
        const url = `${BACKEND}${data.url}`;
        setForm((f) => ({ ...f, images: [...(f.images || []), url], image_url: f.image_url || url }));
      }
      toast.success(files.length > 1 ? `${files.length} photos uploaded` : "Photo uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const onDropImg = (target) => {
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from === null || from === target) return;
    setForm((f) => {
      const imgs = [...(f.images || [])];
      const [moved] = imgs.splice(from, 1);
      imgs.splice(target, 0, moved);
      return { ...f, images: imgs, image_url: imgs[0] };
    });
  };

  const openFeed = () => { setFeedUrl(user?.feed_url || ""); setFeedPreview(null); setFeedFile(null); setFeedHeaders([]); setFeedMapping({}); setFeedOpen(true); };

  const runPreview = async (mapping, fileArg) => {
    const useFile = fileArg !== undefined ? fileArg : feedFile;
    setFeedBusy(true);
    try {
      let data;
      if (useFile) {
        const fd = new FormData();
        fd.append("file", useFile);
        if (mapping) fd.append("mapping", JSON.stringify(mapping));
        ({ data } = await api.post("/partner/feed/preview-file", fd));
      } else {
        ({ data } = await api.post("/partner/feed/preview", { url: feedUrl.trim(), mapping: mapping || undefined }));
      }
      setFeedPreview(data); setFeedHeaders(data.headers || []); setFeedMapping(data.mapping || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that feed");
      if (fileArg) setFeedFile(null);
    } finally { setFeedBusy(false); }
  };

  const previewFeed = () => {
    if (!feedUrl.trim()) { toast.error("Paste a feed URL"); return; }
    setFeedFile(null); setFeedPreview(null); runPreview(null, null);
  };

  const previewFeedFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFeedUrl(""); setFeedFile(file); setFeedPreview(null);
    runPreview(null, file);
    if (feedFileRef.current) feedFileRef.current.value = "";
  };

  const applyMapping = () => runPreview(feedMapping);

  const importFeed = async () => {
    setFeedBusy(true);
    try {
      let data;
      const hasMapping = feedMapping && Object.keys(feedMapping).length > 0;
      if (feedFile) {
        const fd = new FormData();
        fd.append("file", feedFile);
        if (hasMapping) fd.append("mapping", JSON.stringify(feedMapping));
        ({ data } = await api.post("/partner/feed/import-file", fd));
      } else {
        ({ data } = await api.post("/partner/feed/import", { url: feedUrl.trim(), mapping: hasMapping ? feedMapping : undefined }));
      }
      toast.success(`Imported ${data.imported} vehicle${data.imported !== 1 ? "s" : ""}${data.skipped ? `, skipped ${data.skipped} duplicate${data.skipped !== 1 ? "s" : ""}` : ""}`);
      setFeedOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setFeedBusy(false); }
  };

  const load = async () => {
    const { data } = await api.get(listEndpoint);
    setVehicles(data);
    if (isPartner) {
      try { const s = await api.get("/partner/sync-status"); setSyncStatus(s.data); } catch { /* ignore */ }
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setEditing(null); setForm(EMPTY); setImportInfo(null); setOpen(true); };
  const openEdit = (v) => { setEditing(v.id); setImportInfo(null); setForm({ ...EMPTY, ...v, msrp: v.msrp ?? "", option_packages: (v.option_packages || []).join(", ") }); setOpen(true); };

  const openImport = () => { setImportText(""); setResults(null); setImportOpen(true); };

  const doFetch = async () => {
    const urls = importText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (!urls.length) { toast.error("Paste at least one dealer link"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/vehicles/extract-bulk", { urls });
      setResults(data);
    } catch { toast.error("Could not fetch those links"); }
    finally { setImporting(false); }
  };

  const applyResult = (data) => {
    setForm({
      ...EMPTY,
      year: data.year || "", make: data.make || "", model: data.model || "", trim: data.trim || "",
      color: data.color || "", msrp: data.msrp ?? "", vin: data.vin || "", stock_number: data.stock_number || "",
      dealer_location: data.dealer_location || "", body_type: data.body_type || "", image_url: data.image_url || "",
      images: data.images && data.images.length ? data.images : (data.image_url ? [data.image_url] : []),
      option_packages: "", description: "", broker_verified: false, availability: "available",
    });
    setEditing(null);
    setImportInfo({ found: data.fields_found || [], confidence: data.confidence, note: data.note });
    setImportOpen(false); setOpen(true);
  };

  const save = async () => {
    if (!form.year || !form.make || !form.model) { toast.error("Year, make, model required"); return; }
    let images = Array.isArray(form.images) ? [...form.images] : [];
    if (form.image_url && !images.includes(form.image_url)) images = [form.image_url, ...images];
    const payload = { ...form, images, year: parseInt(form.year), msrp: form.msrp ? parseFloat(form.msrp) : null, option_packages: form.option_packages ? form.option_packages.split(",").map((s) => s.trim()).filter(Boolean) : [] };
    try {
      if (editing) await api.put(`/vehicles/${editing}`, payload);
      else await api.post("/vehicles", payload);
      toast.success(editing ? "Vehicle updated" : isPartner ? "Vehicle published" : "Vehicle added");
      setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this vehicle from inventory?")) return;
    await api.delete(`/vehicles/${id}`); toast.success("Removed"); load();
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-950 dark:text-slate-50" data-testid="inventory-title">{isPartner ? "My inventory" : "Inventory management"}</h1>
            <p className="mt-1 text-slate-600 dark:text-slate-300">{isPartner ? "List your dealership's in-stock vehicles — they publish instantly with a Dealer Listed badge." : "Add and curate broker-verified vehicles."}</p>
          </div>
          <div className="flex gap-3">
            {isPartner && <Button variant="outline" onClick={openFeed} className="rounded-md border-slate-300 dark:border-slate-700 font-semibold" data-testid="import-feed-btn"><Rss className="mr-1.5 h-4 w-4" /> Import feed</Button>}
            <Button variant="outline" onClick={openImport} className="rounded-md border-slate-300 dark:border-slate-700 font-semibold" data-testid="import-link-btn"><Link2 className="mr-1.5 h-4 w-4" /> Import from link</Button>
            <Button onClick={openNew} className="rounded-md bg-slate-950 font-semibold hover:bg-slate-800" data-testid="add-vehicle-btn"><Plus className="mr-1.5 h-4 w-4" /> Add vehicle</Button>
          </div>
        </div>

        {isPartner && syncStatus && (
          <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4" data-testid="sync-report">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600"><RefreshCw className="h-4 w-4" /></span>
                <div>
                  {syncStatus.last_sync ? (
                    <>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">Last sync · {SYNC_SOURCE[syncStatus.last_sync.source] || syncStatus.last_sync.source} · {timeAgo(syncStatus.last_sync.at)}</p>
                      <p className="mt-1 text-sm text-slate-700 dark:text-slate-300" data-testid="sync-report-summary">
                        <b className="text-emerald-700">{syncStatus.last_sync.imported} added</b>
                        {" · "}<b className="text-red-600">{syncStatus.last_sync.removed} removed</b>
                        {" · "}<span className="text-slate-500 dark:text-slate-400">{syncStatus.last_sync.skipped} duplicates skipped</span>
                        {syncStatus.last_sync.invalid ? <span className="text-slate-500 dark:text-slate-400">{" · "}{syncStatus.last_sync.invalid} invalid</span> : null}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-slate-600 dark:text-slate-300" data-testid="sync-report-empty">No syncs yet — import a feed URL to enable nightly auto-sync.</p>
                  )}
                </div>
              </div>
              <span className="text-xs font-medium text-slate-400 dark:text-slate-500">{syncStatus.feed_url ? "Auto-syncs daily at 3:00 AM PT" : "Set a feed URL to enable nightly auto-sync"}</span>
            </div>
          </div>
        )}

        {vehicles === null ? (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-lg" />)}</div>
        ) : vehicles.length === 0 ? (
          <div className="mt-10 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-12 text-center" data-testid="inventory-empty">
            <Car className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 font-display text-lg font-bold text-slate-900 dark:text-slate-100">No inventory yet</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">{isPartner ? "Add your first vehicle — it goes live instantly." : "Add your first broker-curated vehicle."}</p>
          </div>
        ) : (
          <div className="mt-8 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <tr><th className="px-5 py-3">Vehicle</th><th className="px-5 py-3">MSRP</th><th className="px-5 py-3">Location</th><th className="px-5 py-3">Status</th><th className="px-5 py-3"></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {vehicles.map((v) => (
                  <tr key={v.id} data-testid={`inv-row-${v.id}`} className="hover:bg-slate-50">
                    <td className="px-5 py-3"><p className="font-semibold text-slate-900 dark:text-slate-100">{v.year} {v.make} {v.model}</p><p className="text-xs text-slate-500 dark:text-slate-400">{v.trim} {v.color && `· ${v.color}`}</p></td>
                    <td className="px-5 py-3 font-semibold">{currency(v.msrp)}</td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{v.dealer_location || "—"}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col gap-1">
                        {v.source === "partner" && <span className="inline-flex w-fit items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-bold text-indigo-700" data-testid={`dealer-listed-${v.id}`}><Store className="h-3 w-3" />Dealer Listed</span>}
                        {v.broker_verified ? <span className="inline-flex w-fit items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700"><ShieldCheck className="h-3 w-3" />Verified</span> : (v.source !== "partner" && <span className="text-xs text-slate-400 dark:text-slate-500">Unverified</span>)}
                        {v.availability !== "available" && <span className="w-fit rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-bold text-red-700">Sold</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(v)} data-testid={`edit-${v.id}`}><Pencil className="h-4 w-4 text-slate-500 dark:text-slate-400" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(v.id)} data-testid={`delete-${v.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" aria-describedby={undefined}>
            <DialogHeader><DialogTitle className="font-display">{editing ? "Edit vehicle" : "Add vehicle"}</DialogTitle></DialogHeader>
            {importInfo && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm" data-testid="import-banner">
                <p className="font-semibold text-rose-700">Pre-filled from dealer link · {importInfo.confidence} confidence</p>
                <p className="text-rose-600">{importInfo.found.length ? `Found: ${importInfo.found.join(", ")}.` : "No structured fields found."} Review and correct everything before saving.</p>
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              {[["year", "Year *", "number"], ["make", "Make *", "text"], ["model", "Model *", "text"], ["trim", "Trim", "text"], ["color", "Color", "text"], ["msrp", "MSRP ($)", "number"], ["vin", "VIN", "text"], ["stock_number", "Stock #", "text"], ["dealer_location", "Dealer location", "text"], ["body_type", "Body type", "text"]].map(([k, label, type]) => (
                <div key={k}>
                  <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">{label}</Label>
                  {k === "vin" ? (
                    <div className="mt-1.5 flex gap-2">
                      <Input data-testid="v-vin" value={form.vin} onChange={(e) => set("vin", e.target.value)} className="flex-1" placeholder="17-character VIN" />
                      <Button type="button" variant="outline" onClick={decodeVin} disabled={decoding} className="shrink-0 rounded-md border-slate-300 dark:border-slate-700 font-semibold" data-testid="decode-vin-btn">
                        {decoding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="mr-1 h-3.5 w-3.5" /> Decode</>}
                      </Button>
                    </div>
                  ) : (
                    <Input data-testid={`v-${k}`} type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} className="mt-1.5" />
                  )}
                </div>
              ))}
              <div className="sm:col-span-2">
                <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Primary image URL</Label>
                <div className="mt-1.5 flex gap-2">
                  <Input data-testid="v-image" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className="flex-1" placeholder="https://…" />
                  <Button type="button" variant="outline" onClick={() => { if (form.image_url && !(form.images || []).includes(form.image_url)) setForm((f) => ({ ...f, images: [...(f.images || []), f.image_url] })); }} className="rounded-md border-slate-300 dark:border-slate-700 font-semibold" data-testid="add-gallery-btn">Add</Button>
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Upload photos from your computer</Label>
                <div className="mt-1.5">
                  <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={uploadPhotos} className="hidden" data-testid="photo-file-input" />
                  <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="rounded-md border-slate-300 dark:border-slate-700 font-semibold" data-testid="upload-photo-btn">
                    {uploading ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Uploading…</> : <><Upload className="mr-1.5 h-4 w-4" /> Upload photos</>}
                  </Button>
                  <span className="ml-2 text-xs text-slate-400 dark:text-slate-500">JPG, PNG, WebP · up to 15MB each</span>
                </div>
              </div>
              {form.images && form.images.length > 0 && (
                <div className="sm:col-span-2">
                  <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Photo gallery ({form.images.length}) · drag to reorder — first photo is the primary</Label>
                  <div className="mt-1.5 grid grid-cols-4 gap-2" data-testid="gallery-grid">
                    {form.images.map((img, i) => (
                      <div key={i} draggable onDragStart={() => (dragIdx.current = i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDropImg(i)} className="group relative aspect-video cursor-move overflow-hidden rounded-md border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-800" data-testid={`gallery-item-${i}`}>
                        <img src={img} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.opacity = 0.3; }} />
                        {form.image_url === img && <span className="absolute left-1 top-1 rounded bg-slate-950 px-1.5 py-0.5 text-[10px] font-bold text-white">Primary</span>}
                        <div className="absolute inset-x-0 bottom-0 flex justify-between gap-1 bg-black/60 p-1 opacity-0 transition-opacity group-hover:opacity-100">
                          <button type="button" onClick={() => set("image_url", img)} className="text-[10px] font-semibold text-white hover:underline" data-testid={`set-primary-${i}`}>Primary</button>
                          <button type="button" onClick={() => setForm((f) => { const imgs = f.images.filter((x) => x !== img); return { ...f, images: imgs, image_url: f.image_url === img ? (imgs[0] || "") : f.image_url }; })} className="text-[10px] font-semibold text-red-300 hover:underline" data-testid={`remove-img-${i}`}>Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="sm:col-span-2"><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Option packages (comma sep)</Label><Input data-testid="v-options" value={form.option_packages} onChange={(e) => set("option_packages", e.target.value)} className="mt-1.5" /></div>
              <div className="sm:col-span-2"><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Description</Label><Textarea data-testid="v-desc" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="mt-1.5" /></div>
              {!isPartner && (
                <div className="flex items-center justify-between rounded-md border border-slate-200 dark:border-slate-800 p-3">
                  <div><p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Broker verified</p><p className="text-xs text-slate-400 dark:text-slate-500">Show verified badge</p></div>
                  <Switch checked={form.broker_verified} onCheckedChange={(v) => set("broker_verified", v)} data-testid="v-verified" />
                </div>
              )}
              <div className="flex items-center justify-between rounded-md border border-slate-200 dark:border-slate-800 p-3">
                <div><p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Available</p><p className="text-xs text-slate-400 dark:text-slate-500">Toggle off if sold</p></div>
                <Switch checked={form.availability === "available"} onCheckedChange={(v) => set("availability", v ? "available" : "sold")} data-testid="v-available" />
              </div>
            </div>
            <DialogFooter><Button onClick={save} className="rounded-md bg-slate-950 font-semibold hover:bg-slate-800" data-testid="save-vehicle-btn">{editing ? "Save changes" : isPartner ? "Publish vehicle" : "Add vehicle"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" aria-describedby={undefined}>
            <DialogHeader><DialogTitle className="font-display">Import from dealer link</DialogTitle></DialogHeader>
            {!results ? (
              <>
                <p className="text-sm text-slate-600 dark:text-slate-300">Paste one or more California dealer inventory listing URLs (one per line). We'll fetch each and preview what we can extract — you review and confirm before publishing.</p>
                <Label className="mt-2 text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Dealer listing URL(s)</Label>
                <Textarea data-testid="import-url-input" value={importText} onChange={(e) => setImportText(e.target.value)} rows={5} placeholder={"https://dealer1.example.com/inventory/abc\nhttps://dealer2.example.com/vehicle/xyz"} className="mt-1.5" />
                <DialogFooter>
                  <Button onClick={doFetch} disabled={importing} className="rounded-md bg-slate-950 font-semibold hover:bg-slate-800" data-testid="do-import-btn">{importing ? "Fetching…" : "Fetch & preview"}</Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{results.length} result{results.length !== 1 ? "s" : ""} — review each before adding</p>
                  <Button variant="ghost" onClick={() => setResults(null)} data-testid="import-back-btn" className="text-slate-600 dark:text-slate-300">Fetch more</Button>
                </div>
                <div className="mt-3 space-y-3">
                  {results.map((rr, i) => (
                    <div key={i} data-testid={`import-result-${i}`} className="flex gap-4 rounded-lg border border-slate-200 dark:border-slate-800 p-4">
                      <div className="h-24 w-32 shrink-0 overflow-hidden rounded-md border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-800">
                        {rr.image_url ? <img src={rr.image_url} alt="preview" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} /> : <div className="flex h-full w-full items-center justify-center text-slate-300"><Car className="h-8 w-8" /></div>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-base font-bold text-slate-900 dark:text-slate-100">{[rr.year, rr.make, rr.model].filter(Boolean).join(" ") || "Unrecognized listing"}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-300">{rr.trim} {rr.color && `· ${rr.color}`} {rr.msrp ? `· ${currency(rr.msrp)}` : ""}</p>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${rr.confidence === "high" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : rr.confidence === "medium" ? "border-amber-200 bg-amber-50 text-amber-700" : "border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-500 dark:text-slate-400"}`}>{rr.confidence} confidence</span>
                          {(rr.fields_found || []).map((f) => <span key={f} className="rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 py-0.5 text-[11px] font-medium text-slate-500 dark:text-slate-400">{f}</span>)}
                        </div>
                        <a href={rr.url} target="_blank" rel="noopener noreferrer" className="mt-1.5 block truncate text-xs font-medium text-rose-600 hover:underline">{rr.url}</a>
                        {rr.images && rr.images.length > 1 && (
                          <div className="mt-1.5 flex items-center gap-1" data-testid={`result-gallery-${i}`}>
                            {rr.images.slice(0, 4).map((im, ix) => <img key={ix} src={im} alt="" className="h-8 w-10 rounded border border-slate-200 dark:border-slate-800 object-cover" onError={(e) => { e.target.style.display = "none"; }} />)}
                            <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">{rr.images.length} photos</span>
                          </div>
                        )}
                      </div>
                      <Button onClick={() => applyResult(rr)} data-testid={`use-result-${i}`} className="shrink-0 self-center rounded-md bg-slate-950 font-semibold hover:bg-slate-800">Review &amp; add</Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={feedOpen} onOpenChange={setFeedOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" aria-describedby={undefined}>
            <DialogHeader><DialogTitle className="font-display">Import from feed URL</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600 dark:text-slate-300">Paste a hosted CSV or JSON inventory feed, or upload a spreadsheet from your computer. We read it directly — no scraping. New vehicles publish instantly under your dealership.</p>
            <div className="mt-2 flex gap-2">
              <Input data-testid="feed-url-input" value={feedUrl} onChange={(e) => { setFeedUrl(e.target.value); setFeedFile(null); }} placeholder="https://yourdealer.com/inventory.csv" className="flex-1" />
              <Button onClick={previewFeed} disabled={feedBusy} className="shrink-0 rounded-md bg-slate-950 font-semibold hover:bg-slate-800" data-testid="feed-preview-btn">{feedBusy && !feedPreview ? "Reading…" : "Preview"}</Button>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" /><span className="text-xs font-semibold uppercase text-slate-400 dark:text-slate-500">or</span><div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
            </div>
            <div className="mt-2 flex items-center gap-2">
              <input ref={feedFileRef} type="file" accept=".csv,.json,text/csv,application/json" onChange={previewFeedFile} className="hidden" data-testid="feed-file-input" />
              <Button variant="outline" onClick={() => feedFileRef.current?.click()} disabled={feedBusy} className="rounded-md border-slate-300 dark:border-slate-700 font-semibold" data-testid="feed-file-btn"><Upload className="mr-1.5 h-4 w-4" /> Upload CSV / JSON file</Button>
              {feedFile && <span className="truncate text-xs font-semibold text-slate-600 dark:text-slate-300" data-testid="feed-file-name">{feedFile.name}</span>}
            </div>
            {feedPreview && (
              <>
                {feedHeaders.length > 0 && (
                  <details className="mt-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-3" data-testid="feed-mapping">
                    <summary className="cursor-pointer select-none text-sm font-bold text-slate-700 dark:text-slate-300">
                      Column mapping <span className="font-medium text-slate-400 dark:text-slate-500">· {Object.values(feedMapping).filter(Boolean).length}/{FEED_FIELDS.length} matched — adjust if a column looks wrong</span>
                    </summary>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {FEED_FIELDS.map(([field, label]) => (
                        <label key={field} className="flex items-center justify-between gap-2 text-xs">
                          <span className="font-semibold text-slate-600 dark:text-slate-300">{label}</span>
                          <select
                            data-testid={`map-${field}`}
                            value={feedMapping[field] || ""}
                            onChange={(e) => setFeedMapping((m) => ({ ...m, [field]: e.target.value }))}
                            className="min-w-0 flex-1 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs"
                          >
                            <option value="">— none —</option>
                            {feedHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                          </select>
                        </label>
                      ))}
                    </div>
                    <Button size="sm" variant="outline" onClick={applyMapping} disabled={feedBusy} className="mt-3 rounded-md border-slate-300 dark:border-slate-700 font-semibold" data-testid="apply-mapping-btn">
                      {feedBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />} Apply mapping
                    </Button>
                  </details>
                )}
                <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-emerald-700" data-testid="feed-count-new">{feedPreview.counts.new} new</span>
                  <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-amber-700">{feedPreview.counts.duplicate} duplicate</span>
                  <span className="rounded-full border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 px-2.5 py-1 text-slate-500 dark:text-slate-400">{feedPreview.counts.invalid} invalid</span>
                </div>
                <div className="mt-3 max-h-72 space-y-2 overflow-y-auto">
                  {feedPreview.rows.map((r, i) => (
                    <div key={i} data-testid={`feed-row-${i}`} className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                      <div className="h-12 w-16 shrink-0 overflow-hidden rounded-md border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-800">
                        {r.image_url ? <img src={r.image_url} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} /> : <div className="flex h-full w-full items-center justify-center text-slate-300"><Car className="h-5 w-5" /></div>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-slate-900 dark:text-slate-100">{[r.year, r.make, r.model].filter(Boolean).join(" ") || "Unrecognized"}</p>
                        <p className="truncate text-xs text-slate-500 dark:text-slate-400">{r.trim} {r.msrp ? `· ${currency(r.msrp)}` : ""} {r._reason && `· ${r._reason}`}</p>
                      </div>
                      <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-bold ${r._status === "new" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : r._status === "duplicate" ? "border-amber-200 bg-amber-50 text-amber-700" : "border-red-200 bg-red-50 text-red-700"}`}>{r._status}</span>
                    </div>
                  ))}
                </div>
                <DialogFooter>
                  <Button onClick={importFeed} disabled={feedBusy || feedPreview.counts.new === 0} className="rounded-md bg-slate-950 font-semibold hover:bg-slate-800" data-testid="feed-import-btn">{feedBusy ? "Importing…" : `Import ${feedPreview.counts.new} vehicle${feedPreview.counts.new !== 1 ? "s" : ""}`}</Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
